import { isoOfCountryName } from './country-lookup';
import { MAP_COUNTRIES } from './map-geometry';
import type {
  ContributorCountry,
  MapGeometry,
  MapRing,
  MapViewBox,
  PaintedCountry,
  UnmappedCountry,
} from './types';

const RADIANS = Math.PI / 180;
/** The parallel the projection keeps true: the middle of the drawn continent. */
const STANDARD_LATITUDE = 8;
const LONGITUDE_SCALE = Math.cos(STANDARD_LATITUDE * RADIANS);

/** The frame the map opens on, whatever the data: Africa, with a little air. */
const AFRICA_BOUNDS = { minLon: -19, maxLon: 53, minLat: -36, maxLat: 38 } as const;

/** Steps of the sequential ramp, above "no data". */
export const RAMP_STEPS = 5;

/** Equirectangular, true at the standard parallel: y grows downwards, as SVG does. */
export function projectPoint(longitude: number, latitude: number): { x: number; y: number } {
  return { x: longitude * LONGITUDE_SCALE, y: -latitude };
}

function ringToPath(ring: MapRing): string {
  const segments = ring.map(([longitude, latitude], index) => {
    const { x, y } = projectPoint(longitude, latitude);
    return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
  });
  return `${segments.join('')}Z`;
}

/** One SVG path for a country, holes and islands included. */
export function geometryToPath(geometry: MapGeometry): string {
  const polygons =
    geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  return polygons.flatMap((polygon) => polygon.map(ringToPath)).join('');
}

/**
 * Which step of the ramp a count earns. The square root keeps a dominant
 * country from flattening every other one into the palest step, without
 * pretending the gap is smaller than it is.
 */
export function stepOf(value: number, highest: number): number {
  if (value <= 0 || highest <= 0) return 0;
  const ratio = Math.sqrt(value / highest);
  return Math.min(RAMP_STEPS, Math.max(1, Math.ceil(ratio * RAMP_STEPS)));
}

export const MAP_VIEW_BOX: MapViewBox = {
  x: AFRICA_BOUNDS.minLon * LONGITUDE_SCALE,
  y: -AFRICA_BOUNDS.maxLat,
  width: (AFRICA_BOUNDS.maxLon - AFRICA_BOUNDS.minLon) * LONGITUDE_SCALE,
  height: AFRICA_BOUNDS.maxLat - AFRICA_BOUNDS.minLat,
};

export interface PaintedMap {
  /** Every bundled country, those without contributors included. */
  readonly countries: readonly PaintedCountry[];
  /** Reported countries no bundled outline covers: listed, never dropped. */
  readonly unmapped: readonly UnmappedCountry[];
  readonly total: number;
  readonly highest: number;
}

/** Turns what the platform reports into what the map can draw. */
export function paintMap(reported: readonly ContributorCountry[]): PaintedMap {
  const byIso = new Map<string, { value: number; reportedName: string }>();
  const unmapped: UnmappedCountry[] = [];
  let total = 0;

  for (const entry of reported) {
    const value = Number(entry.value) || 0;
    if (value <= 0) continue;
    total += value;

    const iso = isoOfCountryName(entry.name);
    const known = iso ? byIso.get(iso) : undefined;
    if (!iso) {
      unmapped.push({ reportedName: entry.name, value, share: 0 });
    } else if (known) {
      byIso.set(iso, { value: known.value + value, reportedName: known.reportedName });
    } else {
      byIso.set(iso, { value, reportedName: entry.name });
    }
  }

  const highest = Math.max(0, ...[...byIso.values()].map((entry) => entry.value));
  const share = (value: number) => (total > 0 ? value / total : 0);

  const countries = MAP_COUNTRIES.map((country) => {
    const found = byIso.get(country.iso2);
    const value = found?.value ?? 0;
    const anchorPoint = projectPoint(country.anchor[1], country.anchor[0]);
    return {
      iso2: country.iso2,
      name: country.name,
      reportedName: found?.reportedName ?? country.name,
      value,
      share: share(value),
      step: stepOf(value, highest),
      path: geometryToPath(country.geometry),
      anchor: anchorPoint,
    } satisfies PaintedCountry;
  });

  return {
    countries,
    unmapped: unmapped.map((entry) => ({ ...entry, share: share(entry.value) })),
    total,
    highest,
  };
}

/** The countries that carry contributions, strongest first. */
export function rankCountries(map: PaintedMap): readonly PaintedCountry[] {
  return map.countries.filter((country) => country.value > 0).sort((a, b) => b.value - a.value);
}
